/**
 * Worker liest sein eigenes Profil (Read-only).
 * Edit-Link sichtbar nur solange draft/rejected — danach ist das Profil gesperrt
 * und Änderungen müssen über Internal Staff laufen.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { DataCard } from '../components/DataCard.js';
import { DocumentUploader } from '../components/DocumentUploader.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { useAuth } from '../hooks/useAuth.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/worker/profile')({
  component: WorkerProfile,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['worker']);
  },
});

function WorkerProfile() {
  const { logout } = useAuth();
  const profile = trpc.workers.getMine.useQuery();

  if (profile.isLoading) {
    return <p className="p-6 text-[var(--muted-foreground)]">Lade Profil …</p>;
  }
  if (profile.error || !profile.data) {
    return (
      <p className="p-6 text-[oklch(0.55_0.2_25)]">
        Fehler: {profile.error?.message ?? 'Profil nicht gefunden'}
      </p>
    );
  }

  const w = profile.data;
  const editable = w.status === 'draft' || w.status === 'rejected';
  const fullName = [w.firstName, w.lastName].filter(Boolean).join(' ');

  return (
    <div className="min-h-screen p-6 max-w-4xl mx-auto space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/worker"
            className="text-sm text-[var(--muted-foreground)] hover:text-foreground"
          >
            ← Worker-Bereich
          </Link>
          <h1 className="text-3xl font-bold mt-2">Mein Profil</h1>
          <p className="text-[var(--muted-foreground)] mt-1">
            Status: <StatusBadge status={w.status} />
            {' · '}
            zuletzt geändert {new Date(w.updatedAt).toLocaleString('de-DE')}
          </p>
        </div>
        <div className="flex gap-2">
          {editable && (
            <Link to="/worker/onboarding">
              <Button>Profil bearbeiten</Button>
            </Link>
          )}
          <Button variant="outline" onClick={() => logout()}>
            Abmelden
          </Button>
        </div>
      </header>

      {w.status === 'rejected' && (
        <Card className="border-[oklch(0.55_0.2_25)]/40">
          <CardHeader>
            <CardTitle>Profil abgelehnt</CardTitle>
            <CardDescription>
              Internal Staff hat dein Profil zurückgewiesen. Bitte korrigiere die Punkte und
              reiche erneut ein.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {w.reviewNotes || 'Keine Detailnotiz hinterlegt — bitte Internal Staff kontaktieren.'}
            </p>
          </CardContent>
        </Card>
      )}

      {w.status === 'pending_review' && (
        <Card>
          <CardContent className="pt-6 text-sm text-[var(--muted-foreground)]">
            Profil ist eingereicht und wird geprüft. Du wirst per E-Mail benachrichtigt,
            sobald die Prüfung abgeschlossen ist.
          </CardContent>
        </Card>
      )}

      {w.status === 'approved' && (
        <Card>
          <CardContent className="pt-6 text-sm text-[var(--muted-foreground)]">
            Profil freigegeben. Änderungen sind nur über Internal Staff möglich.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <DataCard
          title="Persönlich"
          rows={[
            ['Name', fullName],
            ['Geburtsname', w.maidenName],
            ['Geburtsdatum', w.dateOfBirth],
            ['Geburtsort', w.placeOfBirth],
            ['Staatsangehörigkeit', w.nationality],
            [
              'Adresse',
              [w.addressStreet, w.addressZip, w.addressCity, w.addressCountry]
                .filter(Boolean)
                .join(', '),
            ],
            ['Telefon', w.phone],
          ]}
        />
        <DataCard
          title="Steuer & Bank"
          rows={[
            ['Steuer-ID', w.taxId],
            ['Steuerklasse', w.taxClass],
            ['Konfession', w.religiousAffiliation],
            ['Familienstand', w.maritalStatus],
            ['Kinder', w.numberOfChildren?.toString()],
            ['Kinderfreibeträge', w.childAllowances?.toString()],
            ['IBAN', w.iban],
            ['BIC', w.bic],
            ['Kontoinhaber', w.bankAccountHolder],
          ]}
        />
        <DataCard
          title="Sozialversicherung & Aufenthalt"
          rows={[
            ['SV-Nummer', w.socialSecurityNumber],
            ['Krankenkasse', w.healthInsuranceProvider],
            ['Versicherungsstatus', w.insuranceStatus],
            ['Aufenthaltstitel', w.residencePermitType],
            ['Aufenthalt bis', w.residencePermitExpiry],
            [
              'Arbeitserlaubnis bestätigt',
              w.workAuthorizationConfirmed == null
                ? null
                : w.workAuthorizationConfirmed
                  ? 'Ja'
                  : 'Nein',
            ],
            ['Einschränkungen', w.workRestrictions],
          ]}
        />
        <DataCard
          title="Schwerbehinderung & Qualifikationen"
          rows={[
            [
              'Schwerbehindert',
              w.severeDisability == null ? null : w.severeDisability ? 'Ja' : 'Nein',
            ],
            ['Grad der Behinderung', w.disabilityDegree?.toString()],
            ['Gültig bis', w.disabilityValidUntil],
            ['Höchster Abschluss', w.highestEducation],
            ['Führerschein', w.drivingLicenseClass],
          ]}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dokumente</CardTitle>
          <CardDescription>
            {editable
              ? 'Du kannst weitere Dokumente hochladen oder ersetzen.'
              : 'Read-only — Änderungen sind nur über Internal Staff möglich.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentUploader ownerType="worker" ownerId={w.id} readOnly={!editable} />
        </CardContent>
      </Card>
    </div>
  );
}
